/**
 * Lanzador y contenedor del panel, dentro de un shadow root cerrado.
 *
 * Cerrado porque este código corre en la página de otro: uno abierto dejaría que cualquier script
 * del cliente alcance los nodos de dentro. Al documento del cliente solo se añade un `<div>` vacío.
 */

import { DEFAULT_PRIMARY, readableForeground } from '../shared/color';
import { LAUNCHER_STYLES } from './styles';

const OPEN_ICON = `<svg class="icon-open" viewBox="0 0 24 24" fill="none" aria-hidden="true">
<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8A2.5 2.5 0 0 1 17.5 16H9l-4 4v-4.5A2.5 2.5 0 0 1 4 13.5v-8Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
</svg>`;

export interface LauncherLabels {
  open: string;
  close: string;
  frame: string;
}

export interface LauncherOptions {
  panelUrl: string;
  labels: LauncherLabels;
  onOpen: () => void;
  onClose: () => void;
}

export class Launcher {
  private readonly root: ShadowRoot;
  private readonly button: HTMLButtonElement;
  private readonly options: LauncherOptions;

  private frame: HTMLIFrameElement | null = null;
  private avatar: HTMLImageElement | null = null;
  private open = false;



  constructor(options: LauncherOptions) {
    this.options = options;

    const host = document.createElement('div');
    this.root = host.attachShadow({ mode: 'closed' });

    applyStyles(this.root);

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'launcher';
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-label', options.labels.open);

    // Solo el icono de abrir: con el panel abierto el lanzador se oculta y nunca hace de cerrar.
    this.button.innerHTML = OPEN_ICON;
    this.button.addEventListener('click', () => this.toggle());

    this.root.appendChild(this.button);

    document.body.appendChild(host);

    // Escape cierra también desde la página del cliente: dentro del iframe lo gestiona el panel,
    // pero con el foco fuera ese evento no llega hasta aquí.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.open) this.close();
    });
  }

  /** Pinta con el color de marca y calcula el color del icono que va encima. */
  setPrimaryColor(color: string | null): void {
    const primary = color ?? DEFAULT_PRIMARY;

    const style = (this.root.host as HTMLElement).style;
    style.setProperty('--atlas-primary', primary);
    style.setProperty('--atlas-on-primary', readableForeground(primary));
  }

  /**
   * Muestra el avatar del agente en lugar del icono. Sin avatar, o si la imagen no carga, se queda
   * el icono de siempre: una URL caída no puede dejar la burbuja con una imagen rota.
   */
  setAvatar(url: string | null): void {
    if (this.avatar?.src === url) return;

    this.avatar?.remove();
    this.avatar = null;
    this.button.classList.remove('has-avatar');

    if (!url) return;

    const image = document.createElement('img');
    image.className = 'avatar';
    image.alt = '';
    image.addEventListener('error', () => {
      if (this.avatar !== image) return;

      image.remove();
      this.avatar = null;
      this.button.classList.remove('has-avatar');
    }, { once: true });
    image.src = url;

    this.avatar = image;
    this.button.classList.add('has-avatar');
    this.button.appendChild(image);
  }

  /** El iframe que contiene el panel, si ya se ha creado. */
  get panelWindow(): Window | null {
    return this.frame?.contentWindow ?? null;
  }



  toggle(): void {
    if (this.open) this.close();
    else this.show();
  }

  private show(): void {
    // El iframe se crea al abrir por primera vez, no al cargar la página: quien nunca pulsa no
    // paga el panel entero (PERF-03).
    if (!this.frame) this.frame = this.createFrame();

    this.open = true;
    this.frame.classList.add('is-open');
    this.button.classList.add('is-hidden');
    this.button.setAttribute('aria-expanded', 'true');
    this.button.setAttribute('aria-label', this.options.labels.close);

    this.options.onOpen();
  }

  close(): void {
    this.open = false;
    this.frame?.classList.remove('is-open');
    this.button.classList.remove('is-hidden');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-label', this.options.labels.open);

    // Sin devolver el foco al lanzador se queda en un iframe invisible y la navegación por
    // teclado se pierde (A11Y-02).
    this.button.focus();

    this.options.onClose();
  }

  private createFrame(): HTMLIFrameElement {
    const frame = document.createElement('iframe');

    frame.className = 'frame';
    frame.title = this.options.labels.frame;
    frame.src = this.options.panelUrl;

    // `allow-same-origin` es necesario para que el panel use su almacenamiento y hable con el API.
    //
    // `allow-popups` deja abrir en una pestaña nueva, que es como salen los enlaces del texto y los
    // de las fichas: sin él el navegador los bloquea en silencio y no pasa nada al pulsarlos. Va con
    // `allow-popups-to-escape-sandbox` para que la pestaña abierta no herede este sandbox y el sitio
    // de destino funcione con normalidad.
    //
    // Sigue sin `allow-top-navigation`: una pestaña nueva es aceptable, sacar al visitante de la web
    // del cliente no.
    frame.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox'
    );

    this.root.appendChild(frame);

    return frame;
  }
}

/**
 * Aplica los estilos por CSSOM, con `<style>` como reserva: la hoja construida no pasa por el
 * `style-src` de la CSP del cliente. La reserva es para Safari anterior a 16.4.
 */
function applyStyles(root: ShadowRoot): void {
  if (typeof CSSStyleSheet !== 'undefined' && 'replaceSync' in CSSStyleSheet.prototype) {
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(LAUNCHER_STYLES);
      root.adoptedStyleSheets = [sheet];

      return;
    } catch {
      // Cae a la reserva.
    }
  }

  const style = document.createElement('style');
  style.textContent = LAUNCHER_STYLES;
  root.appendChild(style);
}
